import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import LogicFlow from '@logicflow/core';
import { DynamicGroup, MiniMap } from '@logicflow/extension';

import '@logicflow/core/dist/index.css';
import '@logicflow/extension/dist/index.css';
import EdgeTypeDialog from './EdgeTypeDialog';
import NodeEditDialog from './NodeEditDialog';
import ImportDataDialog from './ImportDataDialog';
import OPLPanel from './OPLPanel';
import SearchPanel from './SearchPanel';
import JSONPanel from './JSONPanel';
import {
  TriangleFilledEdge,
  TriangleFilledEdgeModel,
  TriangleHollowEdge,
  TriangleHollowEdgeModel,
  CircleFilledEdge,
  CircleFilledEdgeModel,
  CircleHollowEdge,
  CircleHollowEdgeModel,
  DiamondFilledEdge,
  DiamondFilledEdgeModel,
  DiamondHollowEdge,
  DiamondHollowEdgeModel,
  MiddleArrowEdge,
  MiddleArrowEdgeModel,
  MiddleArrowDoubleTriangleEdge,
  MiddleArrowDoubleTriangleEdgeModel,
  MiddleArrowHollowTriangleEdge,
  MiddleArrowHollowTriangleEdgeModel,
  MiddleArrowTriangleCircleEdge,
  MiddleArrowTriangleCircleEdgeModel,
  BidirectionalArrowEdge,
  BidirectionalArrowEdgeModel,
} from './CustomEdges';
import { Spin } from 'antd';
import './styles.css';
import { nodeTypes, GroupType } from './types/nodeTypes';
import {
  CustomRectNode,
  CustomRectNodeModel,
  CustomCircleNode,
  CustomCircleNodeModel,
  DiamondNode,
  DiamondNodeModel,
  PolygonNode,
  PolygonNodeModel,
  StateNode,
  StateNodeModel,
  ValueNode,
  ValueNodeModel,
  CustomDynamicGroupNode,
  CustomDynamicGroupNodeModel,
} from './nodes';

import { useOPMOperations } from './hooks/useOPMOperations';
import { useOPMViewControl } from './hooks/useOPMViewControl';
import { useOPMFileOperations } from './hooks/useOPMFileOperations';
import { useOPMHighlight } from './hooks/useOPMHighlight';
import { useOPMEdgeOperations } from './hooks/useOPMEdgeOperations';
import { useOPMNodeOperations } from './hooks/useOPMNodeOperations';
import { useViewportCulling } from './hooks/useViewportCulling';
import { useGraphSyncWorker } from './hooks/useGraphSyncWorker';
import { useRightClickSelection } from './hooks/useRightClickSelection';

import { getEdgeText } from './utils/edgeHelpers';
import FloatingToolbar from './FloatingToolbar';
import TopLeftPanel from './TopLeftPanel';
import TopRightPanel from './TopRightPanel';
import NodeListPanel from './NodeListPanel';
import OntologyModelPanel from './OntologyModelPanel';
import type {
  OntologyGraphTree,
  OntologyGraphLayer,
  OPMGraphData,
  OntologyGraphPayload,
} from '../../types/opm';
import {
  normalizeGraphTree,
  findLayerById,
  updateLayerById,
  cloneGraphData,
  generateChildLayerMeta,
  appendChildLayer,
  updateNodeInLayer,
  collectChildLayersByNodeId,
  removeLayersByNodeId,
} from './utils/graphLayerHelpers';
import {
  calculateCanvasSize,
  ensureDynamicGroupAnchorsVisible,
  isDynamicGroupTextClick,
} from './utils/logicFlowHelpers';
import {
  adjustStateNodePositionsForTargetLayer,
  cloneNodeDeep,
  toSafeNumber,
} from './utils/nodeSyncHelpers';

const extractTextValue = (text: any) => {
  if (text && typeof text === 'object') {
    return text.value;
  }
  return text;
};

const isLegacyGraphData = (data: any): data is OPMGraphData => {
  return (
    data &&
    !Array.isArray(data) &&
    Array.isArray(data.nodes) &&
    Array.isArray(data.edges)
  );
};

const mergeNodeAttributes = (targetNode: any, sourceNode: any) => {
  const nextNode = { ...targetNode };
  const sourceValue = extractTextValue(sourceNode.text);
  if (sourceValue !== undefined) {
    const baseText =
      nextNode.text && typeof nextNode.text === 'object'
        ? { ...nextNode.text }
        : {
            value: extractTextValue(nextNode.text),
            x: nextNode.x,
            y: nextNode.y,
          };
    nextNode.text = {
      ...baseText,
      value: sourceValue,
    };
  }

  const preservedFlags = {
    isSubgraphRoot: nextNode.properties?.isSubgraphRoot,
    linkedLayerId: nextNode.properties?.linkedLayerId,
    hasChildSubgraph: nextNode.properties?.hasChildSubgraph,
  };

  nextNode.properties = {
    ...nextNode.properties,
    ...sourceNode.properties,
    ...preservedFlags,
  };

  return nextNode;
};

interface OPMEditorProps {
  className?: string;
  initialData?: OntologyGraphTree | OPMGraphData | OntologyGraphPayload;
  onSave?: (graphData: OntologyGraphTree) => void;
  isSaving?: boolean;
  debug?: boolean;
}

// 渲染计数器和性能监控
let renderCount = 0;
let lastRenderTime = 0;

const OPMEditor: React.FC<OPMEditorProps> = ({
  className = '',
  initialData,
  onSave,
  isSaving = false,
  debug = false,
}) => {
  renderCount++;
  const now = performance.now();
  const timeSinceLastRender = now - lastRenderTime;
  // 仅在渲染间隔超过1秒或渲染耗时过长时打印
  if (timeSinceLastRender > 1000 || renderCount % 20 === 0) {
    console.log(`🔄 OPMEditor 渲染次数: ${renderCount}`);
  }
  lastRenderTime = now;

  const canvasRef = useRef<HTMLDivElement>(null);
  const lfRef = useRef<LogicFlow | null>(null);
  const initialTreeRef = useRef<OntologyGraphTree>(
    normalizeGraphTree(initialData)
  );
  const [graphTree, setGraphTree] = useState<OntologyGraphTree>(
    initialTreeRef.current
  );
  const [selectedLayerId, setSelectedLayerId] = useState<string>(
    initialTreeRef.current[0]?.layerId || '1'
  );
  const selectedLayerIdRef = useRef<string>(selectedLayerId);
  const previousLayerIdRef = useRef<string>(selectedLayerId);

  const [currentGraphData, setCurrentGraphDataState] = useState<OPMGraphData>(
    () => ({
      ...cloneGraphData(initialTreeRef.current[0]?.graphData),
      _timestamp: Date.now(),
    })
  );

  const legacyInitialGraph = useMemo(
    () => (isLegacyGraphData(initialData) ? initialData : undefined),
    [initialData]
  );

  const [nodeCounter, setNodeCounter] = useState(0);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isLogicFlowReady, setIsLogicFlowReady] = useState(false);

  // 面板状态管理
  const [activePanel, setActivePanel] = useState<'data' | 'opl' | 'search'>(
    'data'
  );

  // 全局元素创建顺序计数器
  const [globalElementOrder, setGlobalElementOrder] = useState(0);

  // 元素添加历史记录（按添加顺序）
  const [elementAddHistory, setElementAddHistory] = useState<
    Array<{
      id: string;
      type: 'node' | 'edge';
      timestamp: number;
    }>
  >([]);

  // 搜索相关状态
  const [savedViewState, setSavedViewState] = useState<any>(null);

  // 小地图状态
  const [showMiniMap, setShowMiniMap] = useState(false);

  // 图层数据加载状态
  const [isLayerLoading, setIsLayerLoading] = useState(false);

  // OPL面板侧边栏状态
  const [showOPLSidebar, setShowOPLSidebar] = useState(false);

  // 防止拖放重复创建的标记
  const isCreatingNodeRef = useRef(false);

  // updateGraphData 防抖定时器
  const updateGraphDataTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const syncLinkedNodes = useCallback(
    (tree: OntologyGraphTree, layerId: string, graphData: OPMGraphData) => {
      const layer = findLayerById(tree, layerId);
      if (!layer) return tree;

      const candidateIds = new Set<string>();
      if (layer.parentNodeId) candidateIds.add(layer.parentNodeId);
      layer.children?.forEach(child => {
        if (child.parentNodeId) candidateIds.add(child.parentNodeId);
      });

      if (!candidateIds.size) return tree;

      // 收集状态节点
      const stateNodeMap = new Map<string, any[]>();
      // 收集值节点
      const valueNodeMap = new Map<string, any[]>();

      graphData.nodes.forEach(node => {
        const parentGroupId = node.properties?.parentGroupId;
        if (parentGroupId && candidateIds.has(parentGroupId)) {
          if (node.properties?.isState) {
            const list = stateNodeMap.get(parentGroupId) || [];
            list.push(node);
            stateNodeMap.set(parentGroupId, list);
          } else if (node.properties?.isValue) {
            const list = valueNodeMap.get(parentGroupId) || [];
            list.push(node);
            valueNodeMap.set(parentGroupId, list);
          }
        }
      });

      // 通用的子节点同步函数，支持状态节点和值节点
      const syncChildNodesForLayer = (
        currentTree: OntologyGraphTree,
        targetLayerId: string | undefined,
        parentNodeId: string,
        sourceStates: any[],
        sourceValues: any[],
        sourceParentNode?: any
      ) => {
        if (!targetLayerId) return currentTree;

        return updateLayerById(currentTree, targetLayerId, targetLayer => {
          const existingNodes = targetLayer.graphData?.nodes || [];
          const targetParentNode = existingNodes.find(
            node => node.id === parentNodeId
          );

          // 处理状态节点
          const positionedStates = adjustStateNodePositionsForTargetLayer(
            sourceStates,
            sourceParentNode,
            targetParentNode
          );
          // 处理值节点（使用相同的位置调整逻辑）
          const positionedValues = adjustStateNodePositionsForTargetLayer(
            sourceValues,
            sourceParentNode,
            targetParentNode
          );

          // 合并所有子节点
          const allSourceChildren = [...positionedStates, ...positionedValues];
          const desiredChildIds = allSourceChildren.map(child => child.id);
          const desiredChildIdSet = new Set(desiredChildIds);
          const positionedChildMap = new Map(
            allSourceChildren.map(child => [child.id, child])
          );

          const nextNodes: any[] = [];
          const syncedChildIds = new Set<string>();
          const removedChildIds = new Set<string>();

          existingNodes.forEach(node => {
            if (node.id === parentNodeId) {
              nextNodes.push({
                ...node,
                children: desiredChildIds,
              });
              return;
            }

            // 检查是否是关联的状态节点或值节点
            const isLinkedChild =
              (node.properties?.isState || node.properties?.isValue) &&
              node.properties?.parentGroupId === parentNodeId;

            if (!isLinkedChild) {
              nextNodes.push(node);
              return;
            }

            if (!desiredChildIdSet.has(node.id)) {
              removedChildIds.add(node.id);
              return;
            }

            const sourceChild = positionedChildMap.get(node.id);
            if (sourceChild) {
              nextNodes.push(mergeNodeAttributes(node, sourceChild));
              syncedChildIds.add(node.id);
              return;
            }

            nextNodes.push(node);
          });

          allSourceChildren.forEach(childNode => {
            if (!syncedChildIds.has(childNode.id)) {
              nextNodes.push(cloneNodeDeep(childNode));
            }
          });

          const nextEdges = removedChildIds.size
            ? (targetLayer.graphData?.edges || []).filter(
                edge =>
                  !removedChildIds.has(edge.sourceNodeId) &&
                  !removedChildIds.has(edge.targetNodeId)
              )
            : targetLayer.graphData?.edges || [];

          return {
            ...targetLayer,
            graphData: {
              ...targetLayer.graphData,
              nodes: nextNodes,
              edges: nextEdges,
            },
          };
        });
      };

      let nextTree = tree;
      candidateIds.forEach(nodeId => {
        const sourceNode = graphData.nodes.find(node => node.id === nodeId);
        if (!sourceNode) return;
        const linkedStates = stateNodeMap.get(nodeId) || [];
        const linkedValues = valueNodeMap.get(nodeId) || [];

        if (layer.parentNodeId === nodeId && layer.parentLayerId) {
          nextTree = updateNodeInLayer(
            nextTree,
            layer.parentLayerId,
            nodeId,
            target => mergeNodeAttributes(target, sourceNode)
          );
          nextTree = syncChildNodesForLayer(
            nextTree,
            layer.parentLayerId,
            nodeId,
            linkedStates,
            linkedValues,
            sourceNode
          );
        }

        layer.children?.forEach(child => {
          if (child.parentNodeId === nodeId) {
            nextTree = updateNodeInLayer(
              nextTree,
              child.layerId,
              nodeId,
              target => mergeNodeAttributes(target, sourceNode)
            );
            nextTree = syncChildNodesForLayer(
              nextTree,
              child.layerId,
              nodeId,
              linkedStates,
              linkedValues,
              sourceNode
            );
          }
        });
      });

      return nextTree;
    },
    []
  );

  useEffect(() => {
    selectedLayerIdRef.current = selectedLayerId;
  }, [selectedLayerId]);

  // Web Worker 图树同步 - 将耗时操作移到后台线程
  const { isReady: isWorkerReady, syncGraphTree: workerSyncGraphTree } =
    useGraphSyncWorker();

  // 使用 ref 跟踪 Worker 状态，避免闭包问题
  const isWorkerReadyRef = useRef(false);
  useEffect(() => {
    isWorkerReadyRef.current = isWorkerReady;
  }, [isWorkerReady]);

  // 防抖定时器ref
  const graphSyncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const lastGraphSyncTimeRef = useRef<number>(0);
  // 交互状态标志：拖拽/缩放期间跳过树同步
  const isInteractingRef = useRef<boolean>(false);
  const pendingSyncRef = useRef<OPMGraphData | null>(null);

  const handleGraphDataSync = useCallback(
    (graphData: OPMGraphData) => {
      const now = Date.now();

      // 立即更新当前图数据状态（轻量级操作）
      const stampedData = {
        ...graphData,
        _timestamp: now,
      };
      setCurrentGraphDataState(stampedData);

      // 交互期间完全跳过树同步，记录待同步数据
      if (isInteractingRef.current) {
        pendingSyncRef.current = graphData;
        return;
      }

      // 在300ms内的重复调用，跳过树同步操作
      if (now - lastGraphSyncTimeRef.current < 300) {
        return;
      }

      // 清除之前的防抖定时器
      if (graphSyncDebounceRef.current) {
        clearTimeout(graphSyncDebounceRef.current);
      }

      // 延迟执行树同步操作
      graphSyncDebounceRef.current = setTimeout(async () => {
        // 再次检查交互状态
        if (isInteractingRef.current) {
          pendingSyncRef.current = graphData;
          return;
        }

        const syncStartTime = performance.now();
        const layerId = selectedLayerIdRef.current;
        if (!layerId) return;

        // 使用 Worker 进行异步同步（如果可用）
        const workerReady = isWorkerReadyRef.current;
        console.log(`🔄 图树同步: isWorkerReady=${workerReady}`);
        if (workerReady) {
          try {
            console.log('🔄 开始 Worker 图树同步...');
            // 获取当前树状态
            setGraphTree(prevTree => {
              // 在 Worker 中处理
              workerSyncGraphTree(prevTree, layerId, graphData)
                .then(result => {
                  const syncTime = performance.now() - syncStartTime;
                  console.log(
                    `✅ Worker 图树同步完成: ${syncTime.toFixed(1)}ms`
                  );
                  // 更新状态
                  setGraphTree(result);
                })
                .catch(err => {
                  console.error('Worker 同步失败:', err);
                });
              return prevTree; // 先返回原树，等 Worker 完成后再更新
            });
          } catch (error) {
            console.error('Worker 同步异常:', error);
          }
        } else {
          // 降级到主线程同步
          setGraphTree(prev => {
            if (!layerId) return prev;
            const updatedTree = updateLayerById(prev, layerId, layer => ({
              ...layer,
              graphData: cloneGraphData(graphData),
            }));
            const result = syncLinkedNodes(updatedTree, layerId, graphData);

            const syncTime = performance.now() - syncStartTime;
            if (syncTime > 50) {
              console.warn(`⚠️ 图树同步耗时: ${syncTime.toFixed(1)}ms`);
            }

            return result;
          });
        }

        lastGraphSyncTimeRef.current = Date.now();
      }, 300); // 300ms防抖
    },
    [syncLinkedNodes, workerSyncGraphTree]
  );

  // 设置交互状态的方法
  const setInteracting = useCallback(
    (interacting: boolean) => {
      isInteractingRef.current = interacting;
      // 交互结束后，如果有待同步数据，执行同步
      if (!interacting && pendingSyncRef.current) {
        const pendingData = pendingSyncRef.current;
        pendingSyncRef.current = null;
        // 延迟执行，避免立即阻塞
        setTimeout(() => {
          handleGraphDataSync(pendingData);
        }, 100);
      }
    },
    [handleGraphDataSync]
  );

  // 视图控制 hooks - 需要在 renderLayerGraph 之前定义，因为 renderLayerGraph 依赖 handleResetView
  const {
    handleZoomIn,
    handleZoomOut,
    handleFitView,
    handleResetView,
    handleSaveViewState,
    handleRestoreViewState,
  } = useOPMViewControl({
    lfRef,
    canvasRef,
    setSavedViewState,
    savedViewState,
  });

  // 视口裁剪 hook - 使用 CSS display:none 方式隐藏视口外节点
  const {
    initViewportCulling,
    updateNodeVisibility,
    showAllNodes,
    cacheNodeDomElements,
  } = useViewportCulling(lfRef, {
    // 禁用自定义视口裁剪，因为 LogicFlow 的 partial: true 已经处理了局部渲染
    enabled: false,
    buffer: 500,
    debounceMs: 150,
    nodeThreshold: 50,
  });

  // 右键框选 hook - 支持右键拖拽框选多个节点
  useRightClickSelection(lfRef, canvasRef, {
    enabled: true,
    isWholeNode: false, // 只要碰到节点就选中
    onSelectionComplete: selectedNodeIds => {
      console.log('🎯 框选完成，选中节点:', selectedNodeIds);
    },
  });

  const renderLayerGraph = useCallback(
    (layer: OntologyGraphLayer) => {
      // 开始加载，显示 loading 状态
      setIsLayerLoading(true);

      // 使用 setTimeout 让 React 先完成 loading 状态的渲染
      // 这样用户能立即看到加载中的提示，而不是页面卡住
      setTimeout(() => {
        if (!lfRef.current || !isLogicFlowReady) {
          setIsLayerLoading(false);
          return;
        }

        // 数据克隆和渲染都在 setTimeout 内执行，避免阻塞 UI 更新
        const dataClone = cloneGraphData(layer.graphData);

        lfRef.current.render(dataClone);

        setCurrentGraphDataState({
          ...dataClone,
          _timestamp: Date.now(),
        });

        // 渲染完成后自动重置视图，确保图形在最佳视角显示
        // 使用 requestAnimationFrame 确保 DOM 更新完成
        requestAnimationFrame(() => {
          if (lfRef.current) {
            handleResetView();
          }
          // 加载完成，隐藏 loading 状态
          setIsLayerLoading(false);
        });
      }, 50); // 延迟 50ms 确保 loading UI 已经渲染
    },
    [
      isLogicFlowReady,
      lfRef,
      handleResetView,
      cacheNodeDomElements,
      updateNodeVisibility,
    ]
  );

  const loadLayerById = useCallback(
    (layerId: string, sourceTree?: OntologyGraphTree) => {
      const treeSource = sourceTree || graphTree;
      const targetLayer = findLayerById(treeSource, layerId) || treeSource[0];
      if (!targetLayer) return;
      renderLayerGraph(targetLayer);
      previousLayerIdRef.current = targetLayer.layerId;
    },
    [graphTree, renderLayerGraph]
  );

  const lastInitialDataRef = useRef<typeof initialData>(initialData);

  useEffect(() => {
    if (initialData === undefined && lastInitialDataRef.current === undefined) {
      return;
    }
    if (initialData === lastInitialDataRef.current) {
      return;
    }

    lastInitialDataRef.current = initialData;
    const normalized = normalizeGraphTree(initialData);
    setGraphTree(normalized);

    const prevLayerId = selectedLayerIdRef.current;
    const fallbackLayer =
      findLayerById(normalized, prevLayerId) || normalized[0];
    const nextLayerId = fallbackLayer?.layerId || '1';
    setSelectedLayerId(nextLayerId);
    if (fallbackLayer) {
      loadLayerById(nextLayerId, normalized);
      setIsInitialDataLoaded(!!(lfRef.current && isLogicFlowReady));
    } else {
      setIsInitialDataLoaded(false);
    }
  }, [initialData, isLogicFlowReady, loadLayerById]);

  useEffect(() => {
    if (!isLogicFlowReady || isInitialDataLoaded) return;
    loadLayerById(selectedLayerIdRef.current || graphTree[0]?.layerId || '1');
    setIsInitialDataLoaded(true);
  }, [graphTree, isInitialDataLoaded, isLogicFlowReady, loadLayerById]);

  useEffect(() => {
    if (!isLogicFlowReady) return;
    if (previousLayerIdRef.current === selectedLayerId) return;
    loadLayerById(selectedLayerId);
  }, [isLogicFlowReady, selectedLayerId, loadLayerById]);

  const persistCurrentLayerGraph = useCallback(() => {
    if (!lfRef.current || !selectedLayerIdRef.current) {
      return graphTree;
    }
    const latestData = cloneGraphData(
      lfRef.current.getGraphData() as OPMGraphData
    );
    const nextTree = updateLayerById(
      graphTree,
      selectedLayerIdRef.current,
      layer => ({
        ...layer,
        graphData: latestData,
      })
    );
    const syncedTree = syncLinkedNodes(
      nextTree,
      selectedLayerIdRef.current,
      latestData
    );
    setGraphTree(syncedTree);
    setCurrentGraphDataState({
      ...latestData,
      _timestamp: Date.now(),
    });
    return syncedTree;
  }, [graphTree, lfRef, syncLinkedNodes]);

  const handleLayerSelect = useCallback(
    (layerId: string) => {
      if (!layerId || layerId === selectedLayerId) return;
      persistCurrentLayerGraph();
      setSelectedLayerId(layerId);
    },
    [persistCurrentLayerGraph, selectedLayerId]
  );

  const handleEnterSubgraph = useCallback(
    (layerId: string) => {
      if (!layerId) return;
      persistCurrentLayerGraph();
      setSelectedLayerId(layerId);
    },
    [persistCurrentLayerGraph]
  );

  const handleCreateSubgraph = useCallback(
    (nodeId: string) => {
      if (!lfRef.current || !nodeId) return;
      const currentLayerId = selectedLayerIdRef.current;
      if (!currentLayerId) return;

      const latestTree = persistCurrentLayerGraph();
      const parentLayer = findLayerById(latestTree, currentLayerId);
      if (!parentLayer) return;

      const existingChild = parentLayer.children?.find(
        child => child.parentNodeId === nodeId
      );
      if (existingChild) {
        setSelectedLayerId(existingChild.layerId);
        return;
      }

      const graphData = lfRef.current.getGraphData() as OPMGraphData;
      const targetNode = graphData.nodes.find(node => node.id === nodeId);
      if (!targetNode) return;

      const rootNode = cloneNodeDeep(targetNode);
      rootNode.x = typeof rootNode.x === 'number' ? rootNode.x : 480;
      rootNode.y = typeof rootNode.y === 'number' ? rootNode.y : 240;
      rootNode.properties = {
        ...rootNode.properties,
        isSubgraphRoot: true,
        linkedLayerId: parentLayer.layerId,
      };

      // 同步状态节点到子图
      const linkedStateNodes = graphData.nodes
        .filter(
          node =>
            node.properties?.isState &&
            node.properties?.parentGroupId === nodeId
        )
        .map(stateNode => {
          const clonedState = cloneNodeDeep(stateNode);
          clonedState.properties = {
            ...clonedState.properties,
            parentGroupId: nodeId,
          };
          return clonedState;
        });

      // 同步值节点到子图
      const linkedValueNodes = graphData.nodes
        .filter(
          node =>
            node.properties?.isValue &&
            node.properties?.parentGroupId === nodeId
        )
        .map(valueNode => {
          const clonedValue = cloneNodeDeep(valueNode);
          clonedValue.properties = {
            ...clonedValue.properties,
            parentGroupId: nodeId,
          };
          return clonedValue;
        });

      // 合并所有子节点到 rootNode.children
      const allLinkedNodes = [...linkedStateNodes, ...linkedValueNodes];
      if (allLinkedNodes.length) {
        const mergedChildren = Array.from(
          new Set([
            ...(rootNode.children || []),
            ...allLinkedNodes.map(node => node.id),
          ])
        );
        rootNode.children = mergedChildren;
      }

      const { layerId, layerName } = generateChildLayerMeta(parentLayer);
      const newLayer: OntologyGraphLayer = {
        layerId,
        layerName,
        graphData: {
          nodes: [rootNode, ...allLinkedNodes],
          edges: [],
        },
        children: [],
        parentLayerId: parentLayer.layerId,
        parentNodeId: nodeId,
        nodeIdPrefix: nodeId,
      };

      let nextTree = appendChildLayer(
        latestTree,
        parentLayer.layerId,
        newLayer
      );
      nextTree = updateNodeInLayer(
        nextTree,
        parentLayer.layerId,
        nodeId,
        node => ({
          ...node,
          properties: {
            ...node.properties,
            hasChildSubgraph: true,
          },
        })
      );

      setGraphTree(nextTree);
      setSelectedLayerId(layerId);
      loadLayerById(layerId, nextTree);
    },
    [loadLayerById, persistCurrentLayerGraph]
  );

  const handleTreeImport = useCallback(
    (tree: OntologyGraphTree) => {
      setGraphTree(tree);
      const firstLayer = tree[0];
      const nextLayerId = firstLayer?.layerId || '1';
      selectedLayerIdRef.current = nextLayerId;
      previousLayerIdRef.current = nextLayerId;
      setSelectedLayerId(nextLayerId);
      loadLayerById(nextLayerId, tree);
      setIsInitialDataLoaded(true);
    },
    [loadLayerById]
  );

  const getCurrentLayerMeta = useCallback(() => {
    const layerId = selectedLayerIdRef.current || selectedLayerId;
    if (!layerId) return undefined;
    return findLayerById(graphTree, layerId);
  }, [graphTree, selectedLayerId]);

  const isNodeExpandable = useCallback((node: any) => {
    if (!node) return false;
    if (node.type === 'rect' || node.type === 'polygon') return true;
    if (node.type === 'dynamic-group' && node.properties?.isEntityGroup)
      return true;
    return false;
  }, []);

  const getChildLayersByNodeId = useCallback(
    (nodeId: string) => collectChildLayersByNodeId(graphTree, nodeId),
    [graphTree]
  );

  const removeChildLayersByNodeId = useCallback(
    (nodeId: string) => {
      let fallbackLayerId: string | null = null;
      let updatedSnapshot: OntologyGraphTree | null = null;

      setGraphTree(prev => {
        const updated = removeLayersByNodeId(prev, nodeId);
        updatedSnapshot = updated;
        const currentLayerId = selectedLayerIdRef.current;
        if (currentLayerId && !findLayerById(updated, currentLayerId)) {
          fallbackLayerId = updated[0]?.layerId || '1';
          selectedLayerIdRef.current = fallbackLayerId;
        }
        return updated;
      });

      if (fallbackLayerId && updatedSnapshot) {
        setSelectedLayerId(fallbackLayerId);
        loadLayerById(fallbackLayerId, updatedSnapshot);
      }
    },
    [loadLayerById, setSelectedLayerId]
  );

  const {
    updateGraphData,
    setInteracting: setOperationsInteracting, // 重命名避免冲突
    deleteSelectedElements,
    clearGraph,
    getGraphData,
    addNode,
    createNodeAtPosition,
    createRandomNodes,
    createSampleOPMDiagram,
    deleteNodeWithGuard,
  } = useOPMOperations({
    lfRef,
    setCurrentGraphData: handleGraphDataSync,
    setNodeCounter,
    setGlobalElementOrder,
    setElementAddHistory,
    getCurrentLayerMeta,
    getChildLayersByNodeId,
    removeChildLayersByNodeId,
  });

  const { handleExportJSON, handleImportJSON } = useOPMFileOperations({
    setNodeCounter,
    setGlobalElementOrder,
    setElementAddHistory,
    updateGraphData,
    handleResetView,
    getGraphTree: () => graphTree,
    onTreeImport: handleTreeImport,
  });

  // 创建隐藏的文件输入元素用于导入
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 处理导入按钮点击
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const {
    addHighlightStyles,
    removeHighlightStyles,
    handleOPLHighlightElements,
    handleOPLFocusElements,
    handleOPLClearHighlight,
    handleSearchFocusNode,
    handleSearchHighlightPath,
  } = useOPMHighlight({
    lfRef,
  });

  const {
    // 边操作状态
    showEdgeTypeDialog,
    pendingConnection,
    editingEdge,
    isEditingExistingEdge,
    programCreatedEdgeIds,
    // 边操作函数
    handleEdgeDoubleClick,
    handleEdgeTypeConfirm,
    handleEdgeTypeCancel,
    handleEdgeAdd,
    handleEdgeDelete,
    addProgramCreatedEdgeId,
  } = useOPMEdgeOperations({
    lfRef,
    updateGraphData,
    setGlobalElementOrder,
    setElementAddHistory,
  });

  const {
    // 状态
    selectedNodeId,
    selectedNodeIdRef,
    nodeMenuPosition,
    showNodeMenu,
    showMenuDropdown,
    showNodeEditDialog,
    editingNode,
    isDraggingNodeType,
    draggedNodeType,

    // 导入数据弹窗状态
    showImportDataDialog,
    importingNodeId,
    importingNodeName,

    // 节点菜单操作
    hideNodeMenu,
    toggleMenuDropdown,
    handleNodeClick,

    // 节点编辑操作
    handleNodeDoubleClick,
    handleNodeEditConfirm,
    handleNodeEditCancel,

    // 节点操作
    handleDeleteNode,
    handleEditNode,
    handleCopyNode,
    handleAddState,
    handleDeleteState,
    handleAddValue,
    handleDeleteValue,

    // 导入数据操作
    handleOpenImportDataDialog,
    handleCloseImportDataDialog,
    handleImportDataConfirm,

    // 拖拽操作
    handleNodeTypeDragStart,
    handleNodeTypeDragEnd,
    closeNodeEditDialog,
  } = useOPMNodeOperations({
    lfRef,
    updateGraphData,
    setNodeCounter,
    setGlobalElementOrder,
    setElementAddHistory,
    addProgramCreatedEdgeId, // 传递边创建函数
    programCreatedEdgeIds, // 传递Set引用
    getCurrentLayerMeta,
    deleteNodeWithGuard,
  });

  const currentLayerMeta = getCurrentLayerMeta();
  const isSubgraphLayer = Boolean(currentLayerMeta?.parentLayerId);
  const subgraphRootNodeId = useMemo(() => {
    const rootNode = currentGraphData.nodes.find(
      node => node.properties?.isSubgraphRoot
    );
    return rootNode ? rootNode.id : null;
  }, [currentGraphData]);

  // 缓存选中节点的完整信息，避免菜单渲染时重复调用 getGraphData
  const selectedNodeInfo = useMemo(() => {
    if (!selectedNodeId) return null;
    const targetNode = currentGraphData.nodes.find(
      node => node.id === selectedNodeId
    );
    if (!targetNode) return null;

    const nodeGroupType = targetNode.properties?.groupType;
    return {
      node: targetNode,
      isRect: targetNode.type === 'rect' && !targetNode.properties?.isState,
      isStateGroup:
        targetNode.type === 'dynamic-group' &&
        targetNode.properties?.isEntityGroup &&
        (!nodeGroupType || nodeGroupType === GroupType.STATE),
      isValueGroup:
        targetNode.type === 'dynamic-group' &&
        nodeGroupType === GroupType.VALUE,
      isGroupNode: targetNode.type === 'dynamic-group',
      isStateNode:
        targetNode.type === 'state' || targetNode.properties?.isState,
      isValueNode:
        targetNode.type === 'value' || targetNode.properties?.isValue,
      canImport:
        targetNode.type === 'rect' ||
        targetNode.type === 'polygon' ||
        (targetNode.type === 'dynamic-group' &&
          targetNode.properties?.isEntityGroup),
    };
  }, [currentGraphData.nodes, selectedNodeId]);

  const expandMenuConfig = useMemo(() => {
    if (!selectedNodeId || !currentLayerMeta || !selectedNodeInfo) return null;
    const targetNode = selectedNodeInfo.node;

    const childLayer = currentLayerMeta.children?.find(
      child => child.parentNodeId === selectedNodeId
    );

    const isRootNode = targetNode.properties?.isSubgraphRoot;
    const canExpand =
      isNodeExpandable(targetNode) && !childLayer && !isRootNode;

    if (!canExpand && !childLayer) {
      return null;
    }

    return {
      nodeId: selectedNodeId,
      childLayerId: childLayer?.layerId,
      enabled: canExpand || !!childLayer,
    };
  }, [currentLayerMeta, isNodeExpandable, selectedNodeId, selectedNodeInfo]);

  useEffect(() => {
    hideNodeMenu();
    closeNodeEditDialog();
  }, [selectedLayerId, hideNodeMenu, closeNodeEditDialog]);

  // 小地图控制函数
  const toggleMiniMap = () => {
    if (!lfRef.current) return;

    const newShowState = !showMiniMap;
    setShowMiniMap(newShowState);

    if (newShowState) {
      // 显示小地图
      (lfRef.current.extension.miniMap as any)?.show();
    } else {
      // 隐藏小地图
      (lfRef.current.extension.miniMap as any)?.hide();
    }
  };

  // 保存处理函数
  const handleSave = () => {
    if (!onSave) return;
    // 保存前显示所有节点，确保数据完整
    showAllNodes();
    const latestTree = persistCurrentLayerGraph();
    onSave(latestTree);
    // 保存后重新应用视口裁剪
    requestAnimationFrame(() => {
      updateNodeVisibility();
    });
  };

  // 键盘事件处理函数 - 移除日志
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      e.stopPropagation();
      deleteSelectedElements();
    }
  };

  // 全局键盘事件处理函数
  const handleGlobalKeyDown = (e: KeyboardEvent) => {
    // 只在没有输入框获得焦点时处理删除键
    const activeElement = document.activeElement as HTMLElement;
    const isInputFocused =
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true');

    if (!isInputFocused && (e.key === 'Delete' || e.key === 'Backspace')) {
      e.preventDefault();
      deleteSelectedElements();
    }
  };

  // 初始化 LogicFlow
  useEffect(() => {
    if (!canvasRef.current) return;

    console.log('🚀 开始初始化 LogicFlow...');

    // 确保容器有明确的尺寸
    const container = canvasRef.current;

    // 等待下一个事件循环，确保 DOM 完全渲染
    const initializeLogicFlow = () => {
      const { width, height } = calculateCanvasSize(container);

      console.log('容器尺寸:', {
        width,
        height,
        offsetWidth: container.offsetWidth,
        offsetHeight: container.offsetHeight,
      });

      const lf = new LogicFlow({
        container: container,
        width: width,
        height: height,
        // TODO: 性能测试 - 关闭网格渲染
        grid: false,
        // grid: {
        //   size: 10,
        //   visible: true,
        //   type: 'dot',
        //   config: {
        //     color: '#e0e0e0',
        //     thickness: 1,
        //   },
        // },
        background: {
          color: '#f8f9fa',
        },
        keyboard: {
          enabled: true,
        },
        // 画布交互配置
        animation: false, // 禁用动画以提高性能
        // TODO: 性能测试 - 关闭对齐线（拖动时需要计算与所有节点的对齐）
        snapline: false, // 关闭对齐线
        // 关键性能优化：开启局部渲染，只渲染可见区域的元素
        partial: true,
        // 启用节点调整大小功能
        // allowRotate: false,
        allowResize: true,
        // 插件配置
        plugins: [DynamicGroup, MiniMap],
        // 小地图配置
        pluginsOptions: {
          miniMap: {
            width: 200,
            height: 120,
            showEdge: true,
            rightPosition: 10,
            bottomPosition: 10,
            isShowHeader: false,
            isShowCloseIcon: false,
          },
        },
        // 支持的边类型
        edgeType: 'polyline', // 默认边类型
        // 锚点和连线配置
        hoverOutline: false, // 启用悬停轮廓
        nodeSelectedOutline: true, // 启用选中轮廓
        hideAnchors: false, // 确保锚点可见
        adjustEdge: true, // 允许调整边
        adjustEdgeMiddle: false,
        adjustEdgeStartAndEnd: false,
        edgeSelectedOutline: false,
        // 关键配置：强制显示锚点
        anchorStyle: {
          fill: '#FFFFFF',
          stroke: '#1890ff',
          r: 4,
          strokeWidth: 2,
        },
        style: {
          rect: {
            rx: 5,
            ry: 5,
            strokeWidth: 2,
          },
          circle: {
            fill: '#f5f5f5',
            stroke: '#666',
            strokeWidth: 2,
          },
          ellipse: {
            fill: '#dae8fc',
            stroke: '#6c8ebf',
            strokeWidth: 2,
          },
          polygon: {
            fill: '#ffffff',
            stroke: '#3BC3FF',
            strokeWidth: 2,
          },
          diamond: {
            fill: '#ffe6cc',
            stroke: '#d79b00',
            strokeWidth: 2,
          },
          text: {
            color: '#000000',
            fontSize: 24,
          },
        },
        adjustNodePosition: true, // 恢复节点位置调整
        multipleSelectKey: 'ctrl',
        textEdit: false, // 禁用文本编辑，避免与双击边修改类型冲突
        isSilentMode: false,
        stopScrollGraph: true, // 参考Vue版本：禁止鼠标滚轮滚动移动画布
        stopZoomGraph: false,
        stopMoveGraph: false, // 允许画布平移
        // 明确启用画布交互
        disabledPlugins: [], // 确保没有禁用任何插件
      });

      // 注册自定义节点类型

      // 注册自定义矩形节点（覆盖默认的rect类型）
      lf.register({
        type: 'rect',
        view: CustomRectNode,
        model: CustomRectNodeModel,
      });

      // 注册自定义圆形节点（覆盖默认的circle类型）
      lf.register({
        type: 'circle',
        view: CustomCircleNode,
        model: CustomCircleNodeModel,
      });

      lf.register({
        type: 'diamond',
        view: DiamondNode,
        model: DiamondNodeModel,
      });

      lf.register({
        type: 'polygon',
        view: PolygonNode,
        model: PolygonNodeModel,
      });

      lf.register({
        type: 'state',
        view: StateNode,
        model: StateNodeModel,
      });

      // 注册值节点类型
      lf.register({
        type: 'value',
        view: ValueNode,
        model: ValueNodeModel,
      });

      // 注册动态分组节点类型
      lf.register({
        type: 'dynamic-group',
        view: CustomDynamicGroupNode,
        model: CustomDynamicGroupNodeModel,
      });

      // 确保动态分组节点的锚点默认显示
      lf.on('node:add', ({ data }) => {
        if (data.type === 'dynamic-group') {
          ensureDynamicGroupAnchorsVisible(lf, data.id, {
            logLabel: '🎯 新创建的分组节点设置显示锚点:',
          });
        }
      });

      // 监听画布渲染完成事件
      lf.on('graph:rendered', () => {
        // 标记LogicFlow已准备就绪
        setIsLogicFlowReady(true);

        // 确保所有节点都可见且可拖动
        const nodeCount = lf.graphModel.nodes.length;
        let fixedCount = 0;
        lf.graphModel.nodes.forEach((nodeModel: any) => {
          // 强制设置所有节点为可见
          if (nodeModel.visible === false) {
            nodeModel.visible = true;
            fixedCount++;
          }
          // 确保节点可拖动
          if (nodeModel.draggable === false) {
            nodeModel.draggable = true;
            fixedCount++;
          }
          // 确保节点可移动
          if (nodeModel.isAllowMoveNode === false) {
            nodeModel.isAllowMoveNode = true;
            fixedCount++;
          }
        });
        if (fixedCount > 0) {
          console.log(
            `✅ 修复了 ${fixedCount} 个节点的可见/可拖动状态 (共 ${nodeCount} 个节点)`
          );
        }

        // 确保所有分组节点都显示锚点
        lf.graphModel.nodes.forEach((nodeModel: any) => {
          if (nodeModel.type === 'dynamic-group') {
            ensureDynamicGroupAnchorsVisible(lf, nodeModel.id, {});
          }
        });
      });

      // 初始化视口裁剪（大数据量性能优化）
      initViewportCulling(lf);

      console.log('✅ 自定义节点类型注册完成');

      // 注册自定义边类型
      lf.register({
        type: 'triangle-filled',
        view: TriangleFilledEdge,
        model: TriangleFilledEdgeModel,
      });

      lf.register({
        type: 'triangle-hollow',
        view: TriangleHollowEdge,
        model: TriangleHollowEdgeModel,
      });

      lf.register({
        type: 'circle-filled',
        view: CircleFilledEdge,
        model: CircleFilledEdgeModel,
      });

      lf.register({
        type: 'circle-hollow',
        view: CircleHollowEdge,
        model: CircleHollowEdgeModel,
      });

      lf.register({
        type: 'diamond-filled',
        view: DiamondFilledEdge,
        model: DiamondFilledEdgeModel,
      });

      lf.register({
        type: 'diamond-hollow',
        view: DiamondHollowEdge,
        model: DiamondHollowEdgeModel,
      });

      lf.register({
        type: 'middle-arrow',
        view: MiddleArrowEdge,
        model: MiddleArrowEdgeModel,
      });

      lf.register({
        type: 'middle-arrow-double-triangle',
        view: MiddleArrowDoubleTriangleEdge,
        model: MiddleArrowDoubleTriangleEdgeModel,
      });

      lf.register({
        type: 'middle-arrow-hollow-triangle',
        view: MiddleArrowHollowTriangleEdge,
        model: MiddleArrowHollowTriangleEdgeModel,
      });

      lf.register({
        type: 'middle-arrow-triangle-circle',
        view: MiddleArrowTriangleCircleEdge,
        model: MiddleArrowTriangleCircleEdgeModel,
      });

      lf.register({
        type: 'bidirectional-arrow',
        view: BidirectionalArrowEdge,
        model: BidirectionalArrowEdgeModel,
      });

      console.log('✅ 自定义边类型注册完成');

      // 设置节点样式主题
      lf.setTheme({
        rect: {
          fill: '#ffffff',
          stroke: '#70E483',
          strokeWidth: 2,
          color: '#ffffff',
        },
        circle: {
          fill: '#ffffff',
          stroke: '#52c41a',
          strokeWidth: 2,
          color: '#ffffff',
        },
        // 使用官方API设置节点文字样式
        nodeText: {
          color: '#000000',
          fontSize: 24,
          lineHeight: 1.2,
          overflowMode: 'default' as any,
        },
        // 设置锚点样式以确保可见性和层级
        anchor: {
          fill: '#FFFFFF',
          stroke: '#1890ff',
          r: 4,
          strokeWidth: 2,
          hover: {
            fill: '#1890ff',
            stroke: '#FFFFFF',
            strokeWidth: 3,
            r: 5,
          },
        },
        // 设置连线时的虚线样式
        edgeAnimation: {
          stroke: '#1890ff',
          strokeDasharray: '5,5',
        },
      });

      // 监听连线拖拽事件 - 移除日志以提升性能
      lf.on('anchor:dragstart', () => {
        // 移除日志
      });
      lf.on('anchor:drop', () => {
        // 移除日志
      });
      lf.on('anchor:dragend', () => {
        // 移除日志
      });

      // 监听边添加事件 - 统一处理所有边添加
      lf.on('edge:add', ({ data }) => {
        handleEdgeAdd(data);
      });

      // 监听边双击事件 - 移除日志
      lf.on('edge:dbclick', ({ data }) => {
        handleEdgeDoubleClick(data);
      });

      // 监听节点双击事件 - 移除日志
      lf.on('node:dbclick', ({ data }) => {
        handleNodeDoubleClick(data);
      });

      // 监听节点选中状态变化 - 移除日志以提升性能
      lf.on('node:selected', ({ data }) => {
        // 确保分组节点显示锚点
        if (data.type === 'dynamic-group') {
          ensureDynamicGroupAnchorsVisible(lf, data.id, {
            forceRender: false,
          });
        }
      });

      // 监听节点取消选中 - 移除日志
      lf.on('node:unselected', () => {
        // 移除日志以提升性能
      });

      // 监听节点鼠标进入事件 - 使用节流避免频繁触发
      let lastMouseEnterTime = 0;
      lf.on('node:mouseenter', ({ data }) => {
        // 节流：100ms内不重复处理
        const now = Date.now();
        if (now - lastMouseEnterTime < 100) return;
        lastMouseEnterTime = now;

        // 鼠标进入分组节点时显示锚点（不强制渲染，避免性能问题）
        if (data.type === 'dynamic-group') {
          ensureDynamicGroupAnchorsVisible(lf, data.id, {
            forceRender: false, // 改为false，避免强制渲染
          });
        }
      });

      // 监听节点鼠标离开事件（无日志避免影响性能）
      lf.on('node:mouseleave', () => {
        // 移除日志，高频事件会影响性能
      });

      // 监听节点点击事件 - 优化性能
      lf.on('node:click', ({ data, e }) => {
        // 确保节点可见且可拖动（修复视口裁剪导致的问题）
        const nodeModel = lf.getNodeModelById(data.id) as any;
        if (nodeModel) {
          if (nodeModel.visible === false) nodeModel.visible = true;
          if (nodeModel.draggable === false) nodeModel.draggable = true;
        }

        // 检查是否点击了可拖动文字区域
        if (
          data.type === 'dynamic-group' &&
          isDynamicGroupTextClick({
            lfInstance: lf,
            canvasElement: canvasRef.current,
            event: e,
            nodeId: data.id,
          })
        ) {
          // 文字点击事件，不执行普通的节点点击逻辑
          return;
        }

        // 执行普通的节点点击处理
        handleNodeClick(data, e);

        // 确保分组节点显示锚点 - 使用requestAnimationFrame延迟执行，避免阻塞主线程
        if (data.type === 'dynamic-group') {
          requestAnimationFrame(() => {
            ensureDynamicGroupAnchorsVisible(lf, data.id, {
              forceRender: false, // 不强制渲染，提升性能
            });
          });
        }
      });

      // 监听画布点击事件 - 移除日志
      lf.on('blank:click', () => {
        hideNodeMenu();
      });

      // 监听各种图变化事件（除了 edge:add，因为已经在上面处理了）
      lf.on('node:add', () => {
        // 移除日志，使用防抖避免批量操作时频繁调用 updateGraphData
        if (updateGraphDataTimerRef.current) {
          clearTimeout(updateGraphDataTimerRef.current);
        }
        updateGraphDataTimerRef.current = setTimeout(() => {
          updateGraphData();
          updateGraphDataTimerRef.current = null;
        }, 100);
      });
      lf.on('node:delete', ({ data }) => {
        // 如果删除的是当前选中的节点，立即隐藏菜单
        if (selectedNodeIdRef.current === data.id) {
          hideNodeMenu();
        }

        // 如果删除的是正在编辑的节点，关闭编辑弹窗
        if (editingNode && editingNode.id === data.id) {
          handleNodeEditCancel();
        }

        // 清除可能的高亮状态
        try {
          const selectedElements = lfRef.current?.getSelectElements();
          if (
            selectedElements &&
            (selectedElements.nodes?.some((node: any) => node.id === data.id) ||
              selectedElements.edges?.some(
                (edge: any) =>
                  edge.sourceNodeId === data.id || edge.targetNodeId === data.id
              ))
          ) {
            lfRef.current?.clearSelectElements();
            removeHighlightStyles();
          }
        } catch {
          // 忽略错误
        }

        // 从添加历史中移除该节点
        setElementAddHistory(prev => {
          const nodeIndex = prev.findIndex(
            h => h.id === data.id && h.type === 'node'
          );
          if (nodeIndex !== -1) {
            return prev.filter((_, index) => index !== nodeIndex);
          }
          return prev;
        });

        updateGraphData();
      });

      lf.on('edge:delete', ({ data }) => {
        handleEdgeDelete(data);
      });
      lf.on('node:properties-change', updateGraphData);
      lf.on('edge:properties-change', updateGraphData);

      // 交互状态管理：在拖拽/缩放期间暂停树同步和 updateGraphData
      const startInteraction = () => {
        setInteracting(true);
        setOperationsInteracting(true);
      };
      const endInteraction = () => {
        setInteracting(false);
        setOperationsInteracting(false);
      };

      let transformDebounce: ReturnType<typeof setTimeout> | null = null;
      lf.on('graph:transform', () => {
        startInteraction();
        if (transformDebounce) {
          clearTimeout(transformDebounce);
        }
        transformDebounce = setTimeout(() => {
          endInteraction();
        }, 300);
      });

      // 节点拖拽时也暂停
      lf.on('node:drag', () => {
        startInteraction();
      });
      lf.on('node:drop', () => {
        setTimeout(() => endInteraction(), 200);
      });

      // 渲染画布
      lf.render({
        nodes: [],
        edges: [],
      });

      // 确保画布可以平移
      console.log('LogicFlow 配置信息:', {
        stopMoveGraph: lf.options.stopMoveGraph,
        stopZoomGraph: lf.options.stopZoomGraph,
        stopScrollGraph: lf.options.stopScrollGraph,
      });

      lfRef.current = lf;

      // 输出当前配置用于调试
      console.log('✅ LogicFlow 配置信息 (与Vue版本保持一致):', {
        stopScrollGraph: true, // 禁止鼠标滚轮滚动移动画布
        stopZoomGraph: false, // 允许缩放
        stopMoveGraph: false, // 允许拖拽平移画布
        isSilentMode: false, // 非静默模式
        adjustNodePosition: true, // 允许调整节点位置
      });

      // 为画布添加拖放事件监听器
      const canvasElement = canvasRef.current;
      if (canvasElement) {
        // 阻止默认的拖放行为
        const handleDragOver = (e: DragEvent) => {
          e.preventDefault();
          e.dataTransfer!.dropEffect = 'copy';
        };

        const handleDrop = (e: DragEvent) => {
          e.preventDefault();

          // 防止重复创建
          if (isCreatingNodeRef.current) {
            return;
          }

          try {
            const nodeTypeData = e.dataTransfer!.getData('application/json');
            if (!nodeTypeData) return;

            const nodeType = JSON.parse(nodeTypeData);

            // 设置创建标记
            isCreatingNodeRef.current = true;

            // 使用LogicFlow的坐标转换方法
            // 将屏幕坐标转换为画布逻辑坐标
            let canvasX: number, canvasY: number;

            try {
              // 方法1：优先使用LogicFlow的坐标转换API
              const point = lf!.getPointByClient(e.clientX, e.clientY) as any;
              console.log('LogicFlow getPointByClient 返回值:', point);

              // 根据实际返回的格式处理坐标
              if (typeof point === 'object' && point !== null) {
                // 优先使用 canvasOverlayPosition，这是画布坐标
                if (point.canvasOverlayPosition) {
                  canvasX = point.canvasOverlayPosition.x;
                  canvasY = point.canvasOverlayPosition.y;
                }
                // 备用：使用 domOverlayPosition
                else if (point.domOverlayPosition) {
                  canvasX = point.domOverlayPosition.x;
                  canvasY = point.domOverlayPosition.y;
                }
                // 备用：尝试其他可能的属性名
                else {
                  canvasX = point.x || point.canvasX || point.X;
                  canvasY = point.y || point.canvasY || point.Y;
                }

                // 如果还是没有有效值，尝试数组格式
                if (canvasX === undefined && Array.isArray(point)) {
                  canvasX = point[0];
                  canvasY = point[1];
                }

                console.log('LogicFlow坐标转换成功:', {
                  client: { x: e.clientX, y: e.clientY },
                  canvas: { x: canvasX, y: canvasY },
                  point: point,
                  pointType: typeof point,
                });

                // 如果成功获取到坐标，就使用它
                if (
                  canvasX !== undefined &&
                  canvasY !== undefined &&
                  !isNaN(canvasX) &&
                  !isNaN(canvasY) &&
                  isFinite(canvasX) &&
                  isFinite(canvasY)
                ) {
                  createNodeAtPosition(nodeType.type, canvasX, canvasY);
                  // 延迟重置标记，避免快速连续创建
                  setTimeout(() => {
                    isCreatingNodeRef.current = false;
                  }, 100);
                  return; // 成功，直接返回
                }
              }

              throw new Error('LogicFlow API 返回的坐标格式不正确');
            } catch (error) {
              console.log('LogicFlow坐标转换失败，使用备用方法:', error);

              // 方法2：备用方法 - 手动计算相对坐标并应用逆变换
              const canvasRect = canvasElement.getBoundingClientRect();
              const relativeX = e.clientX - canvasRect.left;
              const relativeY = e.clientY - canvasRect.top;

              try {
                // 获取画布的变换信息
                const transform = lf!.getTransform() as any;
                console.log('完整变换信息:', transform);

                // 根据LogicFlow的变换属性名获取缩放和平移值
                const scaleX =
                  transform?.SCALE_X ||
                  transform?.scale ||
                  transform?.scaleX ||
                  1;
                const scaleY =
                  transform?.SCALE_Y ||
                  transform?.scale ||
                  transform?.scaleY ||
                  1;
                const translateX =
                  transform?.TRANSLATE_X || transform?.translateX || 0;
                const translateY =
                  transform?.TRANSLATE_Y || transform?.translateY || 0;

                // 应用逆变换：先减去平移，再除以缩放
                canvasX = (relativeX - translateX) / scaleX;
                canvasY = (relativeY - translateY) / scaleY;

                console.log('手动坐标转换详细信息:', {
                  client: { x: e.clientX, y: e.clientY },
                  relative: { x: relativeX, y: relativeY },
                  transform: { scaleX, scaleY, translateX, translateY },
                  canvas: { x: canvasX, y: canvasY },
                });

                // 验证计算结果的合理性
                if (
                  !isFinite(canvasX) ||
                  !isFinite(canvasY) ||
                  isNaN(canvasX) ||
                  isNaN(canvasY) ||
                  Math.abs(canvasX) > 10000 ||
                  Math.abs(canvasY) > 10000
                ) {
                  console.warn('计算出的坐标值异常，使用相对坐标:', {
                    canvasX,
                    canvasY,
                  });
                  canvasX = relativeX;
                  canvasY = relativeY;
                }
              } catch (transformError) {
                console.log('获取变换信息失败，使用相对坐标:', transformError);
                canvasX = relativeX;
                canvasY = relativeY;
              }

              // 在 catch 块内部创建节点（备用方法）
              createNodeAtPosition(nodeType.type, canvasX, canvasY);
              // 延迟重置标记
              setTimeout(() => {
                isCreatingNodeRef.current = false;
              }, 100);
            }
          } catch (error) {
            console.error('拖放创建节点失败:', error);
            // 出错时也要重置标记
            isCreatingNodeRef.current = false;
          }
        };

        canvasElement.addEventListener('dragover', handleDragOver);
        canvasElement.addEventListener('drop', handleDrop);

        // 为画布容器添加键盘事件监听
        canvasElement.addEventListener('keydown', handleKeyDown);

        // 添加全局键盘事件监听作为备用
        document.addEventListener('keydown', handleGlobalKeyDown);

        // 让画布容器可以获得焦点以接收键盘事件
        canvasElement.setAttribute('tabindex', '0');

        // 清理函数中移除事件监听器
        return () => {
          canvasElement.removeEventListener('dragover', handleDragOver);
          canvasElement.removeEventListener('drop', handleDrop);
          canvasElement.removeEventListener('keydown', handleKeyDown);
          document.removeEventListener('keydown', handleGlobalKeyDown);
          if (lfRef.current) {
            lfRef.current = null;
          }
        };
      }

      console.log('✅ LogicFlow 初始化完成');
    };

    // 使用 setTimeout 确保 DOM 完全渲染后再初始化
    const timeoutId = setTimeout(initializeLogicFlow, 0);

    // 清理函数
    return () => {
      clearTimeout(timeoutId);
      // 清理高亮样式
      removeHighlightStyles();
      if (lfRef.current) {
        lfRef.current = null;
      }
    };
  }, []);

  // 处理初始数据加载 - 确保LogicFlow ready后再加载
  useEffect(() => {
    if (
      !lfRef.current ||
      !legacyInitialGraph ||
      !isLogicFlowReady ||
      isInitialDataLoaded
    ) {
      return;
    }

    console.log('🔄 开始加载旧版初始数据');

    try {
      lfRef.current.render(legacyInitialGraph);
      console.log('✅ 旧版初始数据渲染完成');
      updateGraphData();
      setIsInitialDataLoaded(true);
    } catch (error) {
      console.error('❌ 旧版初始数据加载失败:', error);
    }
  }, [
    legacyInitialGraph,
    updateGraphData,
    isLogicFlowReady,
    isInitialDataLoaded,
  ]);

  return (
    <div className={`opm-editor ${className} ${!debug ? 'modern-layout' : ''}`}>
      {/* Debug模式下显示原有工具栏 */}
      {debug && (
        <div className="toolbar">
          {/* 节点类型选择器 */}
          <div className="node-types">
            <span className="toolbar-label">节点类型：</span>
            {nodeTypes.map(nodeType => (
              <button
                key={nodeType.type}
                onClick={() => addNode(nodeType.type)}
                onDragStart={e => handleNodeTypeDragStart(e, nodeType)}
                onDragEnd={handleNodeTypeDragEnd}
                draggable="true"
                className={`btn node-type-btn ${isDraggingNodeType ? 'dragging' : ''}`}
                style={{ borderColor: nodeType.color }}
              >
                <span className="node-icon">{nodeType.icon}</span>
                {nodeType.name}
              </button>
            ))}
          </div>

          <div className="divider"></div>

          {/* 缩放控制按钮 */}
          <div className="zoom-controls">
            <span className="toolbar-label">缩放控制：</span>
            <button
              onClick={handleZoomIn}
              className="btn btn-zoom"
              title="放大"
            >
              🔍+
            </button>
            <button
              onClick={handleZoomOut}
              className="btn btn-zoom"
              title="缩小"
            >
              🔍-
            </button>
            <button
              onClick={handleFitView}
              className="btn btn-zoom"
              title="适应画布"
            >
              📐
            </button>
            <button
              onClick={handleResetView}
              className="btn btn-zoom"
              title="重置视图"
            >
              🏠
            </button>
            <button
              onClick={toggleMiniMap}
              className={`btn btn-zoom ${showMiniMap ? 'active' : ''}`}
              title="小地图"
            >
              🗺️
            </button>
          </div>

          <div className="divider"></div>

          {/* 操作按钮 */}
          <div className="actions">
            {/* 保存按钮 - 仅在提供保存回调时显示 */}
            {onSave && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-primary btn-save"
                title="保存"
              >
                💾 {isSaving ? '保存中...' : '保存'}
              </button>
            )}

            <button onClick={clearGraph} className="btn">
              清空画布
            </button>

            {/* JSON导入导出按钮 */}
            <div className="json-actions">
              <button
                onClick={handleExportJSON}
                className="btn btn-primary"
                title="导出JSON"
              >
                📥 导出JSON
              </button>
              <label
                className="btn btn-primary json-import-label"
                title="导入JSON"
              >
                📤 导入JSON
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportJSON}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
        </div>
      )}

      {/* 主内容区域 */}
      <div className="main-content">
        {/* 左侧模型+节点面板 */}
        {!debug && (
          <div className="ontology-sidebar">
            <OntologyModelPanel
              treeData={graphTree}
              selectedLayerId={selectedLayerId}
              onSelectLayer={handleLayerSelect}
            />
            <div className="ontology-panel-card node-list-card">
              <div className="ontology-panel-header !mb-0">
                <h3>本体组成元素列表</h3>
              </div>
              <NodeListPanel
                graphData={currentGraphData}
                lfRef={lfRef}
                onHighlightNode={handleSearchFocusNode}
                rootNodeId={subgraphRootNodeId}
                isSubgraph={isSubgraphLayer}
              />
            </div>
          </div>
        )}

        {/* 画布容器 */}
        <div
          className={`canvas-area ${!debug && showOPLSidebar ? 'with-opl-sidebar' : ''}`}
        >
          {/* 数据加载中遮罩 */}
          {(isLayerLoading || !isLogicFlowReady) && (
            <div className="canvas-loading-overlay">
              <Spin size="large" tip="加载中..." />
            </div>
          )}
          <div ref={canvasRef} className="canvas-container"></div>

          {/* 隐藏的文件输入元素 */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImportJSON}
            style={{ display: 'none' }}
          />

          {/* 新布局：悬浮组件 */}
          {!debug && (
            <>
              {/* 右侧悬浮工具栏 */}
              <FloatingToolbar
                onAddNode={addNode}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onResetView={handleResetView}
                onNodeDragStart={handleNodeTypeDragStart}
                onNodeDragEnd={handleNodeTypeDragEnd}
              />

              {/* 左上角搜索和跳数面板 */}
              <TopLeftPanel
                graphData={currentGraphData}
                lfRef={lfRef}
                onSaveViewState={handleSaveViewState}
                onRestoreViewState={handleRestoreViewState}
                onResetView={handleResetView}
              />

              {/* 右上角保存和OPL按钮 */}
              <TopRightPanel
                onSave={handleSave}
                onShowOPL={() => setShowOPLSidebar(true)}
                onExport={handleExportJSON}
                onImport={handleImportClick}
                isSaving={isSaving}
              />
            </>
          )}
        </div>
        {/* OPL语言侧边栏 - 仅在新布局模式下显示 */}
        {!debug && (
          <div className={`opl-sidebar ${showOPLSidebar ? 'visible' : ''}`}>
            <div className="opl-sidebar-header">
              <h3>本体定义语言</h3>
              <button
                className="opl-sidebar-close"
                onClick={() => setShowOPLSidebar(false)}
                title="关闭"
              >
                ×
              </button>
            </div>
            <div className="opl-sidebar-content">
              {showOPLSidebar && (
                <OPLPanel
                  graphData={currentGraphData}
                  elementAddHistory={elementAddHistory}
                  onHighlightElements={handleOPLHighlightElements}
                  onFocusElements={handleOPLFocusElements}
                  onClearHighlight={handleOPLClearHighlight}
                />
              )}
            </div>
          </div>
        )}

        {/* Debug模式下显示右侧面板 */}
        {debug && (
          <div className="right-panel">
            {/* 标签页 */}
            <div className="panel-tabs">
              <button
                onClick={() => setActivePanel('data')}
                className={`tab-button ${activePanel === 'data' ? 'active' : ''}`}
              >
                数据
              </button>
              <button
                onClick={() => setActivePanel('opl')}
                className={`tab-button ${activePanel === 'opl' ? 'active' : ''}`}
              >
                OPL语言
              </button>
              <button
                onClick={() => setActivePanel('search')}
                className={`tab-button ${activePanel === 'search' ? 'active' : ''}`}
              >
                搜索
              </button>
            </div>

            {/* 数据面板 */}
            {activePanel === 'data' && (
              <JSONPanel
                graphData={currentGraphData}
                onRefresh={() => {
                  console.log('🔄 触发JSON数据刷新');
                  updateGraphData();
                }}
              />
            )}

            {/* OPL 面板 */}
            {activePanel === 'opl' && (
              <div className="opl-area">
                <OPLPanel
                  graphData={currentGraphData}
                  elementAddHistory={elementAddHistory}
                  onHighlightElements={handleOPLHighlightElements}
                  onFocusElements={handleOPLFocusElements}
                  onClearHighlight={handleOPLClearHighlight}
                />
              </div>
            )}

            {/* 搜索面板 */}
            {activePanel === 'search' && (
              <div className="search-area">
                <SearchPanel
                  graphData={currentGraphData}
                  lfRef={lfRef}
                  onSaveViewState={handleSaveViewState}
                  onRestoreViewState={handleRestoreViewState}
                  onResetView={handleResetView}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 边类型选择弹窗 */}
      <EdgeTypeDialog
        visible={showEdgeTypeDialog}
        sourceNode={pendingConnection.source}
        targetNode={pendingConnection.target}
        sourceNodeType={
          pendingConnection.source && lfRef.current
            ? lfRef.current.getNodeModelById(pendingConnection.source)?.type ||
              ''
            : ''
        }
        targetNodeType={
          pendingConnection.target && lfRef.current
            ? lfRef.current.getNodeModelById(pendingConnection.target)?.type ||
              ''
            : ''
        }
        currentEdgeType={editingEdge?.type || ''}
        currentEdgeText={getEdgeText(editingEdge)}
        isEditing={isEditingExistingEdge}
        onConfirm={handleEdgeTypeConfirm}
        onCancel={handleEdgeTypeCancel}
      />

      {/* 节点编辑弹窗 */}
      <NodeEditDialog
        visible={showNodeEditDialog}
        nodeData={editingNode}
        onConfirm={handleNodeEditConfirm}
        onCancel={handleNodeEditCancel}
      />

      {/* 导入数据弹窗 */}
      <ImportDataDialog
        visible={showImportDataDialog}
        nodeId={importingNodeId}
        nodeName={importingNodeName}
        onConfirm={handleImportDataConfirm}
        onCancel={handleCloseImportDataDialog}
      />

      {/* 节点操作菜单 */}
      {showNodeMenu && nodeMenuPosition && (
        <div
          className="node-operation-menu"
          style={{
            position: 'fixed',
            left: nodeMenuPosition.x,
            top: nodeMenuPosition.y,
            zIndex: 9999,
          }}
        >
          {/* 三点按钮 */}
          <div className="menu-trigger" onClick={toggleMenuDropdown}>
            <div className="menu-dots">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>

          {/* 下拉菜单 */}
          <div className={`menu-dropdown ${showMenuDropdown ? 'show' : ''}`}>
            <button
              className="menu-item"
              onClick={handleEditNode}
              title="编辑节点"
            >
              <span className="menu-icon">✏️</span>
              编辑
            </button>
            {/* 根据节点类型显示不同的菜单选项 - 使用缓存的 selectedNodeInfo */}
            {selectedNodeInfo?.isRect && (
              <>
                <button
                  className="menu-item"
                  onClick={handleAddState}
                  title="添加状态"
                >
                  <span className="menu-icon">🔘</span>
                  添加状态
                </button>
                <button
                  className="menu-item"
                  onClick={handleAddValue}
                  title="添加值"
                >
                  <span className="menu-icon">🔘</span>
                  添加值
                </button>
              </>
            )}
            {selectedNodeInfo?.isStateGroup && (
              <button
                className="menu-item"
                onClick={handleAddState}
                title="添加状态"
              >
                <span className="menu-icon">🔘</span>
                添加状态
              </button>
            )}
            {selectedNodeInfo?.isValueGroup && (
              <button
                className="menu-item"
                onClick={handleAddValue}
                title="添加值"
              >
                <span className="menu-icon">🔘</span>
                添加值
              </button>
            )}
            {selectedNodeInfo &&
              !selectedNodeInfo.isGroupNode &&
              !selectedNodeInfo.isStateNode &&
              !selectedNodeInfo.isValueNode && (
                <button
                  className="menu-item"
                  onClick={handleCopyNode}
                  title="复制节点"
                >
                  <span className="menu-icon">📋</span>
                  复制
                </button>
              )}
            {expandMenuConfig && (
              <button
                className="menu-item"
                onClick={() =>
                  expandMenuConfig.childLayerId
                    ? handleEnterSubgraph(expandMenuConfig.childLayerId)
                    : handleCreateSubgraph(expandMenuConfig.nodeId)
                }
                disabled={!expandMenuConfig.enabled}
                title={expandMenuConfig.childLayerId ? '进入子图' : '展开子图'}
              >
                <span className="menu-icon">🌐</span>
                {expandMenuConfig.childLayerId ? '进入子图' : '展开子图'}
              </button>
            )}
            {/* 导入数据按钮 - 使用缓存的 selectedNodeInfo */}
            {selectedNodeInfo?.canImport && (
              <button
                className="menu-item"
                onClick={handleOpenImportDataDialog}
                title="导入数据"
              >
                <span className="menu-icon">📥</span>
                导入数据
              </button>
            )}
            <button
              className="menu-item menu-item-danger"
              onClick={handleDeleteNode}
              title="删除节点"
            >
              <span className="menu-icon">🗑️</span>
              删除
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OPMEditor;
